import { useState, useRef, useCallback, useEffect } from 'react'
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 80 }, aspect, mediaWidth, mediaHeight),
    mediaWidth, mediaHeight
  )
}

function ImageCropModal({ file, aspect, onComplete, onCancel, title = 'Crop Image', recommendedSize = '' }) {
  const [crop, setCrop]               = useState()
  const [completedCrop, setCompletedCrop] = useState()
  const [imgSrc, setImgSrc]           = useState('')
  const imgRef = useRef()

  useEffect(() => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setImgSrc(reader.result)
    reader.readAsDataURL(file)
  }, [file])

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget
    const initialCrop = centerAspectCrop(width, height, aspect)
    setCrop(initialCrop)
    setCompletedCrop(initialCrop)
  }, [aspect])

  const getCroppedBlob = useCallback(() => {
    return new Promise((resolve) => {
      const image = imgRef.current
      if (!image || !completedCrop) return resolve(null)

      // Crop coordinates in natural pixels
      const cropX = (completedCrop.x      / 100) * image.naturalWidth
      const cropY = (completedCrop.y      / 100) * image.naturalHeight
      const cropW = (completedCrop.width  / 100) * image.naturalWidth
      const cropH = (completedCrop.height / 100) * image.naturalHeight

      // Canvas at full natural resolution — no devicePixelRatio scaling needed
      // (that only matters for screen rendering, not file output)
      const canvas = document.createElement('canvas')
      canvas.width  = Math.round(cropW)
      canvas.height = Math.round(cropH)

      const ctx = canvas.getContext('2d')
      ctx.imageSmoothingEnabled  = true
      ctx.imageSmoothingQuality  = 'high'

      // Draw: source = natural crop area, destination = full canvas
      ctx.drawImage(
        image,
        cropX, cropY,           // source x, y  (natural pixels)
        cropW, cropH,           // source w, h  (natural pixels)
        0, 0,                   // destination x, y
        canvas.width,           // destination w
        canvas.height           // destination h
      )

      // 0.95 quality — high fidelity JPEG
      canvas.toBlob(resolve, 'image/jpeg', 0.95)
    })
  }, [completedCrop])

  const handleDone = async () => {
    const blob = await getCroppedBlob()
    if (blob) {
      const croppedFile = new File([blob], 'cropped.jpg', { type: 'image/jpeg' })
      onComplete(croppedFile)
    }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.82)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:5000, padding:'16px' }}>
      <div style={{ background:'#fff', borderRadius:'16px', overflow:'hidden', width:'100%', maxWidth:'520px', boxShadow:'0 24px 80px rgba(0,0,0,0.4)' }}>

        {/* Header */}
        <div style={{ padding:'14px 18px', borderBottom:'1px solid #f0f0f0', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:'14px', fontWeight:700, color:'#0a0a0a', fontFamily:'Space Grotesk,sans-serif' }}>{title}</div>
            {recommendedSize && (
              <div style={{ fontSize:'11.5px', color:'#aaa', marginTop:'2px', fontFamily:'Space Grotesk,sans-serif' }}>
                Recommended: {recommendedSize}
              </div>
            )}
          </div>
          <button onClick={onCancel}
            style={{ width:'28px', height:'28px', borderRadius:'50%', background:'#f0f0f0', border:'none', cursor:'pointer', fontSize:'16px', fontWeight:700, color:'#666', display:'flex', alignItems:'center', justifyContent:'center', lineHeight:1 }}>
            ×
          </button>
        </div>

        {/* Crop area */}
        <div style={{ background:'#111', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', minHeight:'260px', maxHeight:'420px', overflowY:'auto' }}>
          {imgSrc ? (
            <ReactCrop
              crop={crop}
              onChange={(_, pct) => setCrop(pct)}
              onComplete={(_, pct) => setCompletedCrop(pct)}
              aspect={aspect}
              minWidth={30}
              minHeight={30}
              keepSelection
            >
              <img
                ref={imgRef}
                src={imgSrc}
                alt="crop"
                onLoad={onImageLoad}
                style={{ maxWidth:'100%', maxHeight:'380px', display:'block' }}
              />
            </ReactCrop>
          ) : (
            <div style={{ color:'#555', fontSize:'13px', fontFamily:'Space Grotesk,sans-serif' }}>Loading image...</div>
          )}
        </div>

        {/* Hint */}
        <div style={{ padding:'8px 18px', background:'#fafafa', borderTop:'1px solid #f0f0f0', textAlign:'center' }}>
          <div style={{ fontSize:'11.5px', color:'#bbb', fontFamily:'Space Grotesk,sans-serif' }}>
            Drag to reposition · Drag corners to resize · Full resolution output
          </div>
        </div>

        {/* Buttons */}
        <div style={{ padding:'14px 18px', display:'flex', gap:'10px' }}>
          <button onClick={onCancel}
            style={{ flex:1, padding:'11px', borderRadius:'10px', border:'1px solid #e0e0e0', background:'#fff', fontSize:'13px', fontWeight:600, color:'#666', cursor:'pointer', fontFamily:'Space Grotesk,sans-serif' }}>
            Cancel
          </button>
          <button onClick={handleDone}
            style={{ flex:1, padding:'11px', borderRadius:'10px', border:'none', background:'#1877F2', fontSize:'13px', fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'Space Grotesk,sans-serif' }}>
            ✓ Use this crop
          </button>
        </div>

      </div>
    </div>
  )
}

export default ImageCropModal